/**
 * The message components `CopilotChatMessageView` renders through its
 * `assistantMessage` / `userMessage` slots.
 *
 * Why our own components rather than `CopilotChatAssistantMessage`: that
 * component's value is its slots (`markdownRenderer`, `toolCallsView`,
 * `toolbar`), and we replace all three — our markdown renderer, our
 * card-vs-trace split, no toolbar. Wrapping it would contribute only a
 * `display:contents` div. What we do take from CopilotKit is the piece with real
 * behaviour: `CopilotChatMessageView` itself — one pass over the messages,
 * id-deduplication with a dev warning, per-role dispatch, and virtualization past
 * 50 messages. `resolveSlotComponent` accepts a plain component for a slot, which
 * is the documented way to substitute one.
 *
 * Also note what is deliberately NOT used: `renderCustomMessages`. It looked like
 * the home for the display-only fields (⏱ badge, file cards, KB labels), but
 * `renderMessageBlock` renders those before/after the message *block* — outside
 * the bubble. Every one of our extras belongs inside a bubble, so the slot is the
 * right place and the side table is read here. `renderCustomMessages` is for
 * content that sits between messages, which is what a session divider would be.
 */

import { useTranslation } from 'react-i18next';
import React, { createContext, useContext } from 'react';
import Avatar from '@amzn/awsui-chat-components/avatar';
import { Icon } from '@amzn/awsui-components-react';
import MarkdownRenderer from '../MarkdownRenderer';
import FileOutputCards, { type FileOutputInfo } from './FileOutputCards';
import ToolTrace, { ToolTraceNote } from './ToolTrace';
import {
  messageText,
  openSegmentStart,
  parseUserContent,
  unfoldNarrationsIfNoAnswer,
  type AssistantBlock,
  type TranscriptMessage,
} from './transcript';

/**
 * Per-message data that does not exist in `agent.messages`.
 *
 * All of it is either client-only (which KB the user was scoped to when they hit
 * send) or derived from persisted history (the ⏱ badge, computed from DynamoDB
 * create_time deltas). Keyed by message id — the same pattern the panel already
 * used for restored thought strings, minus the projection that carried it.
 */
export interface MessageExtras {
  /** Appended to the rendered user text — the "📎 a.csv, b.csv" attachment line. */
  contentSuffix?: string;
  /** Shown once, on the first send after a workspace file is selected. */
  contextFileName?: string;
  /** Shown once, on the first send after binding to an Asset KB. */
  assetScopeName?: string;
  /** Shown once, on the first send after binding to a Use Case KB. */
  ucScopeName?: string;
  /** End-to-end response time. Only present on restored history — a live turn has
   *  no source for it, which is why a fresh answer shows no badge until reload. */
  elapsedS?: number;
  /** Files the agent produced. No producer on the AG-UI path yet (the file_output
   *  channel is unwired — see the contract doc §3.4); kept so finishing that
   *  channel does not also mean rebuilding its consumer. */
  fileOutputs?: FileOutputInfo[];
}

interface TranscriptContextValue {
  extras: Map<string, MessageExtras>;
  /** Split a message's tool calls into approval cards and collapsed trace rows. */
  splitToolCalls: (toolCallIds: string[] | undefined) => {
    gates: React.ReactElement[];
    trace: React.ReactElement[];
  };
  userInitial: string;
  userLabel: string;
  onFileClick: (path: string) => void;
  /**
   * The assistant message currently being produced, or `undefined` when no turn is
   * in flight. Carried on the CONTEXT, not read from the slot's `isRunning` prop,
   * and that is load-bearing.
   *
   * `CopilotChatMessageView` wraps each slot in `React.memo` whose comparator only
   * honours an `isRunning` change when `messages[messages.length - 1].id ===
   * message.id` (`MemoizedAssistantMessage` in
   * `node_modules/@copilotkit/react-core/dist/copilotkit-*.mjs`). For any turn that
   * called a tool, the last transcript entry is a `role:'tool'` row — the merged
   * assistant head sits BEFORE its tool rows (`coalesceAssistantRuns`) — so the
   * assistant is never last, nothing else about it changes when the run ends, the
   * comparator bails, and the slot keeps rendering with `isRunning=true`: an avatar
   * that spins forever and a trace stuck on "Thinking..." that refuses to collapse.
   * Context updates are not blocked by `React.memo`, so this one gets through.
   */
  streamingMessageId?: string;
}

const TranscriptContext = createContext<TranscriptContextValue | null>(null);

export const TranscriptProvider = TranscriptContext.Provider;

function useTranscript(): TranscriptContextValue {
  const ctx = useContext(TranscriptContext);
  if (!ctx) throw new Error('TranscriptSlots rendered outside TranscriptProvider');
  return ctx;
}

/** Props `CopilotChatMessageView` passes to a message slot. */
interface SlotProps {
  message: TranscriptMessage;
  messages?: TranscriptMessage[];
  isRunning?: boolean;
}

export function RockyUserMessage({ message }: SlotProps) {
  const { t } = useTranslation('chat');
  const { extras, userInitial, userLabel } = useTranscript();
  const own = extras.get(message.id);
  const { text, attachedFiles } = parseUserContent(
    messageText(message.content) + (own?.contentSuffix ?? ''),
  );

  return (
    <div className="chat-msg--user">
      <Avatar initials={userInitial} color="default" ariaLabel={t('messages.userAvatar', { name: userLabel })} />
      <div className="chat-msg__user-text">
        {text}
        {attachedFiles.map((fname, i) => (
          <span key={i} className="chat-msg__context-file-label">
            <Icon name="file" size="small" /> {fname}
          </span>
        ))}
        {own?.contextFileName && (
          <span className="chat-msg__context-file-label">
            <Icon name="file" size="small" /> {own.contextFileName}
          </span>
        )}
        {own?.assetScopeName && (
          <span className="chat-msg__context-file-label">
            <Icon name="gen-ai" size="small" /> KB: {own.assetScopeName}
          </span>
        )}
        {own?.ucScopeName && (
          <span className="chat-msg__context-file-label">
            <Icon name="gen-ai" size="small" /> KB: {own.ucScopeName}
          </span>
        )}
      </div>
    </div>
  );
}

/** The ⏱ badge. Under a minute reads as seconds, above it as `Xm Ys`. */
function TimingBadge({ elapsedS }: { elapsedS: number }) {
  const body = elapsedS < 60
    ? `${elapsedS.toFixed(1)}s`
    : (() => {
        const total = Math.round(elapsedS);
        return `${Math.floor(total / 60)}m ${total % 60}s`;
      })();
  return (
    <div className="chat-msg__timing">
      <span style={{ fontSize: '15px' }}>⏱</span> {body}
    </div>
  );
}

export function RockyAssistantMessage({ message }: SlotProps) {
  const { t } = useTranslation('chat');
  const { extras, splitToolCalls, onFileClick, streamingMessageId } = useTranscript();
  const own = extras.get(message.id);
  const text = messageText(message.content);

  // Open the trace, and keep the avatar spinning, only on the message currently
  // being produced. There is no separate streaming bubble any more — the in-flight
  // turn is just the tail of `agent.messages`, so "streaming" is a property of one
  // message rather than of a second render path that has to be kept in sync.
  //
  // Both halves come from the context rather than from `isRunning` + a scan of the
  // `messages` prop, because the memoized slot may not be re-rendered when either
  // changes. See `streamingMessageId`.
  const isStreaming = !!streamingMessageId && streamingMessageId === message.id;

  // Merged runs always carry `blocks`; the fallback covers a message handed
  // straight to this slot without passing through `coalesceAssistantRuns`.
  const merged: AssistantBlock[] = message.blocks
    ?? [
      ...(message.toolCalls?.length
        ? [{ kind: 'trace' as const, toolCallIds: message.toolCalls.map(tc => tc.id) }]
        : []),
      ...(text ? [{ kind: 'text' as const, text }] : []),
    ];
  // Mid-stream an aside stays in the trace; once the turn is over, a turn with no
  // other text gets it back as the answer (see `unfoldNarrationsIfNoAnswer`).
  const unfolded = isStreaming
    ? { blocks: merged, narrations: message.narrations ?? [] }
    : unfoldNarrationsIfNoAnswer(merged, message.narrations ?? []);
  const { narrations } = unfolded;
  // While streaming, the open segment always shows a live trace — right after a
  // HITL resume it has no calls yet, and without this placeholder the only
  // progress signal would be the spinning avatar.
  let blocks = unfolded.blocks;
  const openStart = openSegmentStart(blocks);
  if (isStreaming && !blocks.slice(openStart).some(b => b.kind === 'trace')) {
    blocks = [...blocks.slice(0, openStart), { kind: 'trace', toolCallIds: [] }, ...blocks.slice(openStart)];
  }

  // Each trace block's rows. Narrations go immediately before their tool's row;
  // one whose tool call has no trace row (e.g. filtered out) is appended to the
  // last trace rather than dropped.
  const placed = new Set<number>();
  const traceRows = new Map<number, React.ReactElement[]>();
  let lastTrace = -1;
  blocks.forEach((block, bi) => {
    if (block.kind !== 'trace') return;
    lastTrace = bi;
    const rows: React.ReactElement[] = [];
    for (const id of block.toolCallIds) {
      const row = splitToolCalls([id]).trace;
      if (row.length === 0) continue;
      narrations.forEach((n, i) => {
        if (n.beforeToolCallId === id) {
          placed.add(i);
          rows.push(<ToolTraceNote key={`note-${i}`} text={n.text} />);
        }
      });
      rows.push(...row);
    }
    traceRows.set(bi, rows);
  });
  const unplaced = narrations
    .map((n, i) => ({ n, i }))
    .filter(({ i }) => !placed.has(i))
    .map(({ n, i }) => <ToolTraceNote key={`note-${i}`} text={n.text} />);
  if (unplaced.length > 0) {
    // No trace at all means no trace rows to re-index, and not streaming (that
    // always adds one), so prepending one here shifts nothing that is read later.
    if (lastTrace < 0) {
      blocks = [{ kind: 'trace', toolCallIds: [] }, ...blocks];
      lastTrace = 0;
    }
    traceRows.set(lastTrace, [...(traceRows.get(lastTrace) ?? []), ...unplaced]);
  }

  // Keys by segment, not by index: a segment's trace is inserted at its start
  // when its first call arrives, which would shift every later index and remount
  // the bubble and the ToolTrace (losing a manual collapse) mid-stream.
  let segment = 0;
  const rendered = blocks.map((block, bi) => {
    const seg = segment;
    if (block.kind === 'gates') {
      segment++;
      return (
        <React.Fragment key={`gates-${seg}`}>
          {splitToolCalls(block.toolCallIds).gates}
        </React.Fragment>
      );
    }
    if (block.kind === 'trace') {
      const rows = traceRows.get(bi) ?? [];
      const live = isStreaming && bi >= openStart;
      if (rows.length === 0 && !live) return null;
      return <ToolTrace key={`trace-${seg}`} isStreaming={live}>{rows}</ToolTrace>;
    }
    return (
      <div key={`text-${seg}`} className="chat-msg__bubble">
        <div className="chat-msg__bubble-text">
          <MarkdownRenderer markdown={block.text} />
        </div>
      </div>
    );
  });
  return (
    <div className="chat-msg--ai">
      <div className="chat-msg__ai-avatar-wrap">
        <Avatar
          imgUrl="/icons/avatar-rocky.png"
          color="gen-ai"
          ariaLabel={t('messages.assistantAvatar')}
          loading={isStreaming}
        />
      </div>
      <div className="chat-msg__ai-content">
        {/* The prose and the approval cards interleave, each card after the
            text that set it up — the 20-row sample preview before the button that
            approves that segment, and the reply describing the outcome
            ("收到的选择是：continue") after it. That is also the order CopilotKit's
            own `CopilotChatAssistantMessage` uses (markdown, then that message's
            tool calls); `blocks` is what keeps it true across the merge. Each
            stretch between cards opens with its own collapsed trace, so the work
            done after the user answers a card shows up below that card. */}
        {rendered}
        {own?.fileOutputs && own.fileOutputs.length > 0 && (
          <FileOutputCards files={own.fileOutputs} onFileClick={onFileClick} />
        )}
        {own?.elapsedS != null && <TimingBadge elapsedS={own.elapsedS} />}
      </div>
    </div>
  );
}
